import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { resolveImg } from "@/lib/apiData";

const isExternal = (to) => /^https?:/.test(to || "");

export const CtaLink = ({ to, children, testId }) =>
  isExternal(to)
    ? <a href={to} target="_blank" rel="noreferrer" data-testid={testId}>{children}</a>
    : <Link to={to || "/"} data-testid={testId}>{children}</Link>;

export const HomeBanners = ({ items, t }) => {
  const list = (items || []).filter((b) => b.active && (b.placement || "home") === "home");
  if (!list.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 grid gap-6 md:grid-cols-2" data-testid="home-banners">
      {list.map((b, i) => (
        <div key={i} className={`relative overflow-hidden min-h-[220px] flex items-end group ${list.length === 1 ? "md:col-span-2" : ""}`} data-testid={`home-banner-${i}`}>
          {b.image && <img src={resolveImg(b.image)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-transparent" />
          <div className="relative p-8 sm:p-10 max-w-lg">
            <h3 className="font-display text-3xl font-black text-white">{t(b.title)}</h3>
            {b.subtitle && <p className="text-neutral-200 mt-2">{t(b.subtitle)}</p>}
            {b.button_text && (
              <CtaLink to={b.button_link} testId={`home-banner-btn-${i}`}>
                <Button className="mt-6 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">{t(b.button_text)} <ArrowRight className="w-4 h-4" /></Button>
              </CtaLink>
            )}
          </div>
        </div>
      ))}
    </section>
  );
};

export const CustomSections = ({ sections, t }) => {
  const list = (sections || []).filter((s) => s.active !== false);
  return list.map((s, i) => {
    const imageRight = s.layout === "image-right";
    return (
      <section key={i} className="max-w-7xl mx-auto px-5 sm:px-8 py-16" data-testid={`custom-section-${i}`}>
        <div className={`grid md:grid-cols-12 gap-10 items-center ${!s.image ? "md:grid-cols-1" : ""}`}>
          {s.image && (
            <div className={`md:col-span-6 overflow-hidden aspect-[4/3] ${imageRight ? "md:order-2" : ""}`}>
              <img src={resolveImg(s.image)} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          )}
          <div className={s.image ? "md:col-span-6" : "max-w-3xl"}>
            {s.label && <p className="text-xs uppercase tracking-[0.25em] text-[#d00000] mb-4">{t(s.label)}</p>}
            <h2 className="font-display text-4xl sm:text-5xl font-black text-neutral-900 leading-none">{t(s.title)}</h2>
            {s.text && <p className="mt-5 text-neutral-600 leading-relaxed whitespace-pre-line">{t(s.text)}</p>}
            {s.button_text && (
              <CtaLink to={s.button_link} testId={`custom-section-btn-${i}`}>
                <Button className="mt-8 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">{t(s.button_text)} <ArrowRight className="w-4 h-4" /></Button>
              </CtaLink>
            )}
          </div>
        </div>
      </section>
    );
  });
};

export const Testimonials = ({ items, t }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
    {(items || []).map((x, i) => (
      <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
        className="bg-white border border-neutral-200 p-6" data-testid={`testimonial-${i}`}>
        <Quote className="w-8 h-8 text-[#d00000] mb-4" />
        <p className="text-neutral-700 leading-relaxed">{t(x.text)}</p>
        <div className="mt-5 flex items-center gap-1 mb-2">
          {Array.from({ length: Number(x.rating) || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#f5a623] text-[#f5a623]" />)}
        </div>
        <p className="text-sm text-neutral-900 font-semibold">{x.name}</p>
        <p className="text-xs text-neutral-400">{x.city}</p>
      </motion.div>
    ))}
  </div>
);
